import { useCallback, useEffect, useMemo } from 'react';

import { Box, Divider, Grid, Typography } from '@mui/material';
import { useField } from 'formik';

import { useQuery } from '@redwoodjs/web';

import { InputBaseField } from 'src/components/common/form/FormInput';
import { RadioGroupField } from 'src/components/common/form/FormRadioGroup';
import { OptionTypes } from 'src/components/Dropdown';
import { DropDown } from 'src/components/FormikComponents';
import MainCard from 'src/components/MainCard/MainCard';
import { showErrorToast } from 'src/components/Toasts/toast';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { useProductOrderContext } from 'src/contexts/ProductOrderProvider';
import { useServiceOrderContext } from 'src/contexts/ServiceOrderProvider';
import { GET_PRICING_OFFER } from 'src/graphql/queries/pricing.query';
import { useTransactionPricing } from 'src/hooks';
import { usePricingOffer } from 'src/hooks/usePricingOffer';
import { isApplicationEditable } from 'src/pages/MerchantApplicationPage/merchantApplicationHelpers.util';

import { ContractTerm } from '../../steps/ServiceRequirements';

interface ServiceTypeProps {
    contractTerms: ContractTerm[];
}

export enum ServiceTypes {
    TERMINAL = 'Terminal',
    ONLINE = 'Online',
    ACQUIRING_ONLY = 'Acquiring Only',
}

export enum PricingServiceTypes {
    TERMINAL = 'TERMINAL',
    ONLINE = 'ONLINE',
    ACQUIRING_ONLY = 'ACQUIRING_ONLY',
}

export enum PricingTerms {
    O12_MONTHS = '12_MONTHS',
    O18_MONTHS = '18_MONTHS',
    O24_MONTHS = '24_MONTHS',
    O36_MONTHS = '36_MONTHS',
    O48_MONTHS = '48_MONTHS',
    CUSTOM = 'CUSTOM',
}

export enum ProductType {
    ONLINE = 'ONLINE',
    TERMINAL = 'TERMINAL',
}

export type OfferProduct = {
    id: number;
    name: string;
    type?: ProductType;
    imageUrl?: string;
    price?: number;
    description?: string;
    options?: {
        oneOff?: {
            sales: number;
            commission: number | null;
        }[];
        monthly?: {
            sales: number;
            commission: number | null;
        }[];
    };
};

export const ServiceType: React.FC<ServiceTypeProps> = ({ contractTerms }) => {
    const [{ value: serviceValue }] = useField('service');
    const [{ value: trialPeriodValue }] = useField('trialPeriod');
    const [, , { setValue: setCustomTermValue }] = useField('customTerm');
    const [, , { setValue: setPromoOfferValue }] = useField('service.promoOffer');

    const [
        ,
        { touched: isShouldApplyPromoTouched },
        { setTouched: setShouldApplyPromoTouch, setValue: setShouldApplyPromo },
    ] = useField('service.shouldApplyPromo');
    const { serviceType } = useProductOrderContext();
    const { partnerId, setContractTerm, contractTerm } = useServiceOrderContext();
    const { TRANSACTION_PRICING_OFFER_ID_KEY } = useTransactionPricing(partnerId);
    const { merchantApplication } = useAddMerchantContext();

    const { pricingOfferTerminalOrOnlineOptions, transactionPricingOfferOptions } = usePricingOffer(
        {
            partnerId,
            contractTerm,
            serviceType: serviceValue?.serviceType,
        }
    );

    let selectedOfferValue;
    if (serviceType === PricingServiceTypes.TERMINAL) {
        selectedOfferValue = serviceValue.selectedTerminalOffer;
    } else if (serviceType === PricingServiceTypes.ONLINE) {
        selectedOfferValue = serviceValue.selectedOnlineOffer;
    }

    const { data: pricingOfferData, refetch } = useQuery(GET_PRICING_OFFER, {
        variables: {
            id: parseInt(selectedOfferValue),
        },
        onError: (err) => {
            return showErrorToast(err.message);
        },
        skip: true,
    });

    const handlePricingOfferChange = useCallback(
        async (offerId) => {
            if (offerId) {
                await refetch({ variables: { id: parseInt(offerId) } });
            }
        },
        [refetch]
    );

    const isOnlinePricingOffer = useMemo(() => {
        const serviceType = serviceValue?.serviceType;

        return serviceType === PricingServiceTypes.ONLINE;
    }, [serviceValue]);

    const serviceTypes = useMemo(() => {
        return [
            {
                label: ServiceTypes.TERMINAL,
                value: PricingServiceTypes.TERMINAL,
            },
            {
                label: ServiceTypes.ONLINE,
                value: PricingServiceTypes.ONLINE,
            },
            {
                label: ServiceTypes.ACQUIRING_ONLY,
                value: PricingServiceTypes.ACQUIRING_ONLY,
            },
        ];
    }, []);

    const selectedOfferHasPromo = useMemo(() => {
        let selectedOfferValue;
        if (serviceType === PricingServiceTypes.TERMINAL) {
            selectedOfferValue = serviceValue.selectedTerminalOffer;
        } else if (serviceType === PricingServiceTypes.ONLINE) {
            selectedOfferValue = serviceValue.selectedOnlineOffer;
        }
        const selectedOffer = pricingOfferTerminalOrOnlineOptions.find(({ value }) => {
            return value === selectedOfferValue;
        });
        return selectedOffer?.shouldApplyPromo && trialPeriodValue !== 'true';
    }, [serviceType, pricingOfferTerminalOrOnlineOptions, serviceValue, trialPeriodValue]);

    const contractTermOptions: OptionTypes[] = useMemo(() => {
        if (
            serviceValue?.serviceType === PricingServiceTypes.ONLINE ||
            serviceValue?.serviceType === PricingServiceTypes.TERMINAL
        ) {
            const { shouldApplyPromo } = serviceValue;
            return contractTerms
                .filter((term) => {
                    if (serviceValue?.serviceType === PricingServiceTypes.ONLINE) {
                        return term.online;
                    }
                    return true;
                })
                .map((term) => {
                    if (shouldApplyPromo === 'no' || !selectedOfferHasPromo) {
                        return { value: term.value, label: term.label };
                    }
                    return { value: term.value, label: `${term.label} + 3 Months` };
                });
        }
        return contractTerms.map((term) => {
            return {
                value: term.value,
                label: term.label,
            };
        });
    }, [contractTerms, serviceValue, selectedOfferHasPromo]);

    const serviceTypeOptions = useMemo(() => {
        return serviceTypes.map((option) => {
            return {
                value: option.value,
                label: option.label,
            };
        });
    }, [serviceTypes]);
    const isEditingDisabled = isApplicationEditable(merchantApplication);

    useEffect(() => {
        if (pricingOfferData?.offer?.customTerm) {
            setCustomTermValue(pricingOfferData.offer.customTerm);
        } else {
            setCustomTermValue(undefined);
        }
    }, [pricingOfferData]);

    useEffect(() => {
        if (
            (!selectedOfferHasPromo && !isEditingDisabled && isShouldApplyPromoTouched) ||
            trialPeriodValue === 'true'
        ) {
            setPromoOfferValue(undefined);
            setShouldApplyPromo(undefined);
        }
        // Prevent running on load
        setShouldApplyPromoTouch(true);
    }, [selectedOfferHasPromo, trialPeriodValue]);

    return (
        <MainCard>
            {/* Service Type Section */}
            <Typography fontSize="24px" color="#575757" marginBottom="8px">
                Service Type
            </Typography>
            <Typography fontSize="14px" color="#575757" marginBottom="8px">
                Select merchant service type
            </Typography>
            <Divider
                sx={{
                    borderColor: 'warning.main',
                    marginBottom: '14px',
                }}
            />
            <Typography fontSize="14px" color="#575757" marginBottom="14px">
                <RadioGroupField
                    label="Merchant Offered Trial Period?"
                    containerGridSpacing={3}
                    labelGridSize={3}
                    radioGroupGridSize={3}
                    radioOptionGridSize={3}
                    name="trialPeriod"
                    defaultValue={'false'}
                    options={[
                        {
                            label: 'Yes',
                            value: 'true',
                        },
                        {
                            label: 'No',
                            value: 'false',
                        },
                    ]}
                />
            </Typography>
            {/* Drop downs */}
            <Box>
                <Grid container spacing={3}>
                    {/* Service Type Drop Down */}
                    <Grid item xs={12} md={6}>
                        <DropDown
                            name="service.serviceType"
                            label="Service Type"
                            options={serviceTypeOptions}
                            placeholder="Select Service Type"
                            disabled={isEditingDisabled}
                        />
                    </Grid>
                    {/* Contract Term & Pricing offers */}
                    {serviceType !== PricingServiceTypes.ACQUIRING_ONLY && (
                        <>
                            {/* #1 Contract Term */}
                            <Grid item xs={12} md={6}>
                                <DropDown
                                    options={contractTermOptions}
                                    name="service.contractTerm"
                                    label="Contract Term"
                                    onChange={(value) => {
                                        return setContractTerm(value);
                                    }}
                                    disabled={isEditingDisabled}
                                />
                            </Grid>
                            {/* #2 Terminal or Online Pricing Offer */}
                            <Grid item xs={12} md={6}>
                                <DropDown
                                    name={`${
                                        isOnlinePricingOffer
                                            ? 'service.selectedOnlineOffer'
                                            : 'service.selectedTerminalOffer'
                                    }`}
                                    label={`${
                                        isOnlinePricingOffer ? 'Online' : 'Terminal'
                                    } Pricing Offer`}
                                    options={pricingOfferTerminalOrOnlineOptions}
                                    placeholder={`Select ${
                                        isOnlinePricingOffer ? 'Online' : 'Terminal'
                                    } Pricing Offer`}
                                    disabled={isEditingDisabled}
                                    onChange={(value) => {
                                        return handlePricingOfferChange(value);
                                    }}
                                />
                            </Grid>
                        </>
                    )}
                    {/* Transaction Pricing offer */}
                    <Grid item xs={12} md={6}>
                        <DropDown
                            name={`service.${TRANSACTION_PRICING_OFFER_ID_KEY}`}
                            label="Transaction Pricing Offer"
                            options={transactionPricingOfferOptions}
                            placeholder="Select Transaction Pricing Offer"
                            disabled={isEditingDisabled}
                        />
                    </Grid>
                    {/* Promo offer */}
                    {selectedOfferHasPromo && (
                        <Grid item xs={12} md={6}>
                            <DropDown
                                name="service.shouldApplyPromo"
                                label="Promo Offer?"
                                defaultValue={serviceValue?.shouldApplyPromo ?? 'no'}
                                options={[
                                    { value: 'no', label: 'NO' },
                                    { value: 'yes', label: 'YES' },
                                ]}
                                disabled={isEditingDisabled}
                            />
                        </Grid>
                    )}
                    {serviceValue.shouldApplyPromo == 'yes' && (
                        <Grid item xs={12} md={6}>
                            <DropDown
                                name="service.promoOffer"
                                label="Select Promo Offer"
                                placeholder="Select Promo Offer"
                                options={[
                                    { value: 3, label: '3 Months Free' },
                                    { value: 6, label: '6 Months Half Price' },
                                ]}
                                disabled={isEditingDisabled}
                            />
                        </Grid>
                    )}
                    {pricingOfferData?.offer?.customTerm &&
                    serviceValue?.contractTerm === PricingTerms.CUSTOM ? (
                        <Grid item xs={12} md={6}>
                            <InputBaseField
                                label="Custom Term in Months"
                                name="customTerm"
                                placeholder="Enter Custom Term in Months"
                                value={pricingOfferData?.offer?.customTerm ?? ''}
                                disabled
                            />
                        </Grid>
                    ) : null}
                </Grid>
            </Box>
        </MainCard>
    );
};
